import { useCallback, useEffect, useState } from "react"

/* Getting onto a home screen, and why it takes two answers instead of one.
 *
 * The two platforms reach it by different routes, and only one of them can be driven from code:
 *
 *  - Chromium fires `beforeinstallprompt`, which we keep so our own button can open the real
 *    install dialog at a moment we choose rather than one the browser chooses.
 *  - iOS Safari fires nothing and exposes no install API at all. The player has to go through
 *    Share → Add to Home Screen themselves, so the most we can do is say so at the right moment.
 *
 * Anything else — Chrome or Firefox on iOS included, since neither can install a standalone app
 * there — gets nothing, rather than instructions that lead to a button that is not present.
 *
 * This is `utils/input`'s question, not `utils/viewport`'s: it is about what this browser can do,
 * and a narrow window on a desktop is not a phone. */

const DISMISSED_KEY = "hvz-install-dismissed"
const DISMISSAL_DAYS = 30

/* `beforeinstallprompt` fires **once**, on `window`, and the event it hands over is single-use.
   Two components call this hook — the banner and the settings entry — and whichever mounts first
   would otherwise catch the event into its own state and the other would never know it happened.
   That is not hypothetical: the banner mounts with the app, so on Chromium the settings entry
   never offered Install at all, and on a desktop, where the banner renders nothing, the offer was
   swallowed by a component that had already decided not to show it.

   So the event is held here, once, with a tiny pub/sub — the same shape `utils/shortcuts` uses
   for the same reason. The listener is attached at module load rather than in an effect, because
   Chromium fires this early and an effect that runs after the first paint can miss it. */
let deferredPrompt = null
const listeners = new Set()

const publish = (event) => {
  deferredPrompt = event
  listeners.forEach((listener) => listener(event))
}

/* Forget a prompt that has been spent. Chromium's event may be used exactly once, so this runs
   after the dialog closes — and it is exported because module state outlives a test, so a suite
   that fires the event once would otherwise hand it to every test after it. */
export const forgetInstallPrompt = () => publish(null)

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Chromium would otherwise show its own mini-infobar at a moment of its choosing, which on a
    // game screen lands over the map.
    event.preventDefault()
    publish(event)
  })
  window.addEventListener("appinstalled", () => publish(null))
}

export const isStandalone = () => {
  if (typeof window === "undefined") return false
  // matchMedia is the standard signal; navigator.standalone is the only one iOS sets.
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    window.navigator.standalone === true
  )
}

export const isIos = () => {
  if (typeof window === "undefined") return false
  const ua = window.navigator.userAgent
  // iPadOS 13+ reports itself as a Mac, and a touch-capable Mac does not otherwise exist.
  return (
    /iphone|ipad|ipod/i.test(ua) ||
    (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1)
  )
}

/* A link opened from a chat or mail app runs in that app's own webview, not in Safari. Its share
   sheet has no "Add to Home Screen" at all, so the ordinary iOS instructions send the reader
   hunting for a button that is not there — the only way out is to reopen the page in Safari.

   Detected by what those webviews leave out rather than by name: Safari on iOS always reports
   "Safari/<version>" and an embedded WKWebView does not, which catches the ones nobody thought to
   put on a list. On iOS that is most of them. */
export const isInAppBrowser = () => {
  if (typeof window === "undefined" || !isIos()) return false
  const ua = window.navigator.userAgent
  if (/crios|fxios|edgios|opios/i.test(ua)) return false
  return !/safari\//i.test(ua)
}

/* An Android browser that has not offered its dialog: Chrome before it judges the site worth it,
   Samsung Internet, Firefox. Each has "Install app" or "Add to Home screen" in its own menu, so
   the settings entry can say where, where it could only say nothing before. The banner never
   reads this mode: a menu item is not something to interrupt a game for. */
export const isAndroid = () => typeof window !== "undefined" && /android/i.test(window.navigator.userAgent)

export const isIosSafari = () => {
  if (typeof window === "undefined") return false
  const isAnotherIosBrowser = /crios|fxios|edgios|opios/i.test(window.navigator.userAgent)
  return isIos() && !isAnotherIosBrowser && !isInAppBrowser()
}

const wasRecentlyDismissed = () => {
  try {
    const stored = window.localStorage.getItem(DISMISSED_KEY)
    if (!stored) return false
    const elapsed = Date.now() - Number(stored)
    return Number.isFinite(elapsed) && elapsed < DISMISSAL_DAYS * 24 * 60 * 60 * 1000
  } catch (error) {
    // A browser refusing storage gets the banner every session, which is the safe way round.
    return false
  }
}

export const useInstallPrompt = () => {
  const [deferred, setDeferred] = useState(() => deferredPrompt)
  const [installed, setInstalled] = useState(isStandalone)
  const [dismissed, setDismissed] = useState(wasRecentlyDismissed)

  useEffect(() => {
    // Whatever arrived before this component existed, plus everything after.
    setDeferred(deferredPrompt)
    listeners.add(setDeferred)
    return () => listeners.delete(setDeferred)
  }, [])

  useEffect(() => {
    const onInstalled = () => setInstalled(true)
    window.addEventListener("appinstalled", onInstalled)
    return () => window.removeEventListener("appinstalled", onInstalled)
  }, [])

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now()))
    } catch (error) {
      // Storage refused: the banner still closes for this session, which is what was asked.
    }
    setDismissed(true)
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferred) return null
    await deferred.prompt()
    const { outcome } = await deferred.userChoice
    // Cleared for everybody, not just for whoever pressed the button — the other copy of this
    // hook is looking at the same spent event.
    forgetInstallPrompt()
    if (outcome === "accepted") setInstalled(true)
    else dismiss()
    return outcome
  }, [deferred, dismiss])

  /* What this browser can be *told*, ignoring whether now is the moment to say it. The settings
     pop-up reads this, so a player who waved the banner away can still find the instructions. */
  let mode = "none"
  if (!installed) {
    if (deferred) mode = "prompt"
    else if (isInAppBrowser()) mode = "open-in-safari"
    else if (isIosSafari()) mode = "manual-ios"
    else if (isAndroid()) mode = "manual-android"
  }

  /* Whether to interrupt with the banner. Chromium withholds `beforeinstallprompt` until it
     decides the site is worth installing, so "prompt" is already gated by the browser; the other
     two are gated by the dismissal alone. */
  const promptMode = dismissed || mode === "manual-android" ? "none" : mode

  return { mode, promptMode, installed, promptInstall, dismiss }
}
